import { useState, useEffect } from "react";
import useAuthStore from "../../store/authStore";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import AdminLayout from "../../layouts/AdminLayout";
import { getUserById, getUsers, updateUser, getLocations } from "../../api/accountsService";

function UpdateUser() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const role = useAuthStore((state) => state.role);
  const isAdmin = role === "ADMIN";

  const [loading, setLoading] = useState(true);
  const [managers, setManagers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState("/images/avatar.png");

  const [formData, setFormData] = useState({
    employee_id: "",
    full_name: "",
    email: "",
    mobile_number: "",
    date_of_birth: "",
    gender: "MALE",
    designation: "",
    department: "",
    role: "EMPLOYEE",
    manager_id: "",
    is_active: true,
    photo: null,
    location: "",
    date_joined: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getUserById(employeeId);
        const user = res.data.user;

        setFormData({
          employee_id: user.employee_id || "",
          full_name: user.full_name || "",
          email: user.email || "",
          mobile_number: user.mobile_number || "",
          date_of_birth: user.date_of_birth || "",
          gender: user.gender || "MALE",
          designation: user.designation || "",
          department: user.department || "",
          role: user.role || "EMPLOYEE",
          manager_id: user.manager_id || "",
          is_active: user.is_active || false,
          photo: null,
          location: user.location || "",
          date_joined: user.date_joined ? user.date_joined.split("T")[0] : "",
        });

        if (user.photo) {
          const backendURL = `${process.env.REACT_APP_API_URL || "http://localhost:8000"}`;
          setProfilePhoto(`${backendURL}${user.photo}`);
        }

        if (isAdmin) {
          const allUsersRes = await getUsers();
          const allUsers = allUsersRes.data.users || [];
          const managerList = allUsers.filter((u) => u.role === "MANAGER");
          setManagers(managerList);
        }

        try {
          const locRes = await getLocations();
          setLocations(locRes.data.locations || []);
        } catch (err) {
          console.error("Failed to load locations", err);
        }
      } catch (err) {
        console.error("Failed to fetch user data:", err);
        alert("Failed to fetch user data from backend.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [employeeId, isAdmin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, photo: file }));
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate("/admin/dashboard", { state: { defaultTab: "profile" } });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== "") {
          if (key === "photo" && value instanceof File) {
            payload.append("photo", value);
          } else if ((key === "date_of_birth" || key === "date_joined") && value) {
            payload.append(key, new Date(value).toISOString().split("T")[0]);
          } else if (key === "is_active") {
            payload.append(key, value ? "true" : "false");
          } else {
            payload.append(key, value);
          }
        }
      });
      await updateUser(employeeId, payload);
      alert("User updated successfully ✅");
    } catch (err) {
      console.error("Failed to update user:", err);
      if (err.response?.data) {
        const message = Object.entries(err.response.data)
          .map(([field, msgs]) =>
            `${field}: ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`
          )
          .join("\n");
        alert(message);
      } else {
        alert("Failed to update user");
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-white text-lg sm:text-xl font-semibold">Loading user data...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-5xl mx-auto p-3 sm:p-6"
      >
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg flex-shrink-0">
              <span className="text-2xl sm:text-3xl">👤</span>
            </div>
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-white">Update User</h1>
              <p className="text-white/60 text-xs sm:text-sm">Edit employee information</p>
            </div>
          </div>
        </div>

        <div className="bg-white/15 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-4 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">

            {/* Photo upload */}
            <div className="flex justify-center items-center mb-4 sm:mb-8 flex-col">
              <label className="w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full shadow-xl cursor-pointer hover:scale-105 transition-transform">
                <img
                  src={photoPreview || profilePhoto}
                  alt="Profile"
                  className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-4 border-white/20 shadow-xl"
                  onError={(e) => e.target.src = "/images/avatar.png"}
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </label>
              <p className="text-white text-xs sm:text-sm mt-2">Click on profile to upload photo</p>
            </div>

            <div>
              <SectionHeader icon="👤" title="Personal & Professional Info" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mt-4">
                <Input
                  icon="🆔"
                  label="Employee ID"
                  value={formData.employee_id}
                  disabled
                />
                <Input
                  icon="👤"
                  label="Full Name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                />
                <Input
                  icon="📧"
                  label="Email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <Input
                  icon="📱"
                  label="Mobile Number"
                  type="tel"
                  name="mobile_number"
                  value={formData.mobile_number}
                  onChange={handleChange}
                  placeholder="+91 9876543210"
                />
                <Input
                  icon="🎂"
                  label="Date of Birth"
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                />
                <Select
                  icon="🚻"
                  label="Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  options={[
                    { value: "MALE", label: "Male" },
                    { value: "FEMALE", label: "Female" },
                  ]}
                />
                <Input
                  icon="📅"
                  label="Date of Joining"
                  type="date"
                  name="date_joined"
                  value={formData.date_joined}
                  onChange={handleChange}
                  required
                />
                <Input
                  icon="🏢"
                  label="Department"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  required
                />
                <Input
                  icon="💼"
                  label="Designation"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  required
                />

                {isAdmin && (
                  <Select
                    icon="🎭"
                    label="Role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    options={[
                      { value: "MANAGER", label: "Manager" },
                      { value: "EMPLOYEE", label: "Employee" },
                    ]}
                  />
                )}

                <Select
                  icon="👥"
                  label={formData.manager_id ? "Assigned Manager" : "Assign Manager"}
                  name="manager_id"
                  value={formData.manager_id || ""}
                  onChange={handleChange}
                  required={formData.role === "EMPLOYEE"}
                  options={[
                    { value: "", label: "-- Select Manager --" },
                    ...managers.map((mgr) => ({
                      value: mgr.employee_id,
                      label: `${mgr.employee_id} - ${mgr.full_name}`,
                    })),
                  ]}
                />

                <Select
                  icon="📍"
                  label="Location"
                  name="location"
                  value={formData.location || ""}
                  onChange={handleChange}
                  options={[
                    { value: "", label: "-- Select Location --" },
                    ...locations.map((l) => ({
                      value: l.code,
                      label: `${l.code} - ${l.name}`,
                    })),
                  ]}
                />

                <Select
                  icon="✅"
                  label="Active Status"
                  name="is_active"
                  value={formData.is_active ? "True" : "False"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_active: e.target.value === "True",
                    }))
                  }
                  options={[
                    { value: "True", label: "Active" },
                    { value: "False", label: "Inactive" },
                  ]}
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 pt-6 border-t border-white/10">
              <motion.button
                type="submit"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto px-8 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-blue-500 to-cyan-500 shadow-lg hover:shadow-2xl transition-all duration-300 text-sm"
              >
                💾 Update User
              </motion.button>
              <Link
                to="/admin/users"
                className="w-full sm:w-auto text-center px-8 py-3 rounded-xl font-bold text-white bg-white/10 border-2 border-white/20 hover:bg-white/20 transition-all duration-300 text-sm"
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>

        <button
          onClick={handleBack}
          className="inline-block px-5 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-semibold mt-4 sm:mt-6 text-sm"
        >
          ← Back to Profile
        </button>
      </motion.div>
    </AdminLayout>
  );
}

// Reusable Components
const inputClass = `
  w-full mt-1 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl
  bg-white/15 border-2 border-blue-500/50 focus:border-blue-500
  text-white text-sm font-medium outline-none transition-all duration-300
  backdrop-blur-sm placeholder:text-white/40
  disabled:bg-white/5 disabled:border-white/10 disabled:cursor-not-allowed
`;

const selectClass = `
  w-full mt-1 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl
  bg-white/15 border-2 border-blue-500/50 focus:border-blue-500
  text-white text-sm font-medium outline-none transition-all duration-300
  backdrop-blur-sm appearance-none
`;

function Label({ children, icon }) {
  return (
    <p className="text-xs font-semibold text-white/60 flex items-center gap-2 mb-1 sm:mb-2">
      {icon && <span>{icon}</span>}
      <span className="uppercase tracking-wide">{children}</span>
    </p>
  );
}

function Input({ icon, label, ...props }) {
  return (
    <div>
      <Label icon={icon}>{label}</Label>
      <input {...props} className={inputClass} />
    </div>
  );
}

function Select({ icon, label, options, ...props }) {
  return (
    <div>
      <Label icon={icon}>{label}</Label>
      <select {...props} className={selectClass}>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} className="bg-slate-800">
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function SectionHeader({ icon, title }) {
  return (
    <div className="flex items-center gap-3 mb-3 sm:mb-4">
      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
        <span className="text-lg sm:text-xl">{icon}</span>
      </div>
      <h3 className="text-lg sm:text-xl font-bold text-white">{title}</h3>
    </div>
  );
}

export default UpdateUser;
